import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  growthFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';

// ---------------------------------------------------------------------------
// Lincoln MoneyGuard Fixed Advantage — asset-based hybrid universal life with
// a long-term care benefits rider (LTCBR).
//
// Structurally a two-pool hybrid drawn Face-first then Continuation-of-
// Benefits. The Face/COB split is purely our modeling decomposition of a
// single LTC rider — see the indemnity note below.
//
//   Pool 1 (base) = Face / Specified Amount of Death Benefit
//                   (`policyMaximumBenefitAmount`). On hybrids this field is
//                   the death benefit, which on this product also equals the
//                   LTC acceleration portion (no NYL-ACIO-style face↔accel
//                   divergence because `hasFlatDeathBenefit` is true).
//                   `policyBenefitUtilizedToDate` is drawn down at init, Face
//                   first; any excess spills into the COB pool (same prior-
//                   utilization model as Indexed Annuity Care).
//   Pool 2 (eob)  = Continuation of Benefits (`policyContinuationBenefitAmount`),
//                   drawn only after the Face pool exhausts.
//
// Death benefit: flat Face (`hasFlatDeathBenefit = true` → never inflated).
// LTC reimbursements reduce the death benefit dollar-for-dollar by the FACE
// (base) drawdown only — COB drawdown does NOT reduce the death benefit.
// The opening DB is likewise net of `policyBenefitUtilizedToDate` (a partially
// used policy opens reduced). Floored at the Residual Death Benefit
// (`policyMinimumGuaranteedBenefitAmount`, 5% of Face on this illustration).
//
// Payout model (default reimbursement, indemnity at first-claim election):
//   - Reimbursement: pay min(eligibleCost, proRatedCap), drawn Face then COB.
//   - Indemnity (gated on `isIndemnityPolicyPayment`): the election is
//     CLAIM-WIDE and irrevocable — MoneyGuard has ONE LTC rider, and the
//     Face/COB split is only our modeling decomposition. So the capped
//     indemnity cash is paid from BOTH pools (Face first, then COB) at the
//     SAME indemnity monthly cap, regardless of actual cost, until the total
//     LTC benefit exhausts. It NEVER reverts to reimbursement on the COB
//     share. Verified against the carrier illustration (one indemnity monthly
//     max $3,995 = 80% × $4,993, quoted at year 1 AND age 85 — i.e. deep into
//     the COB pool). This is unlike OA Asset Care, whose AOB-only indemnity /
//     COB-reversion is a genuine product rule for THAT product; MoneyGuard
//     previously (and wrongly) copied it.
//
//     INDEMNITY BENEFIT REDUCTION: Lincoln caps cash indemnity at 80% of the
//     Monthly Maximum ("The indemnity choice option provides 80% of the
//     Maximum Monthly LTC Benefit Amount", illustration p.4 fn 5 / p.9). Like
//     Asset Care's 75% cap, this is a fixed product property carried on the
//     policy as `policyIndemnityBenefitPercent` (the upload field map fixes it
//     at 0.80 — see policyUploadPolicyFieldMap.ts), which the indemnity branch
//     below applies. The legacy aggregate calc applies no such factor, so an
//     uploaded indemnity MoneyGuard diverges from legacy here — the new
//     strategy is the intended-correct path. Default is reimbursement.
//
// Indemnity surplus: like every indemnity product, the indemnity cash that
// exceeds eligible cost banks as a surplus reserve and drains on later
// shortfall months (shared `applyIndemnitySurplus` helper, CM II parity). On
// reimbursement-election policies the surplus is always 0 — a numeric no-op.
//
// Inflation: per-pool growth at policy anniversary, accrued on the at-issue
// amount, gated on pool > 0. Rates: face/base from
// `policyInflationProtectionOnFaceAmount ?? policyInflationProtection`, COB/eob
// from `policyInflationProtectionOnCOBAmount ?? policyInflationProtection`, cap
// `policyInflationProtectionYears`. Lincoln sells the LTCBR inflation option in
// both SIMPLE and COMPOUND forms, so every factor routes through the shared
// `growthFactor` on `isSimpleInflationProtection` (policy-wide — one rider, one
// election) rather than compounding unconditionally. The example illustration
// elected no inflation ("LTCBR Inflation Option: n/a") so every factor is 1.0
// there, but the general inflation path is implemented like OA Asset Care.
//
// Elimination period: 0 days on this product. Served via the two-tier WP
// fields; EP days are out of pocket with no retroactive lump.
// `policyBenefitPeriodMonths` (LTCBR Benefit Duration) is NOT a cap — the
// dollar pools are the cap.
//
// MoneyGuard carries one strategy-state field — the indemnity surplus reserve
// — beyond the universal pools/waitingPeriod/facts/deltas.
// ---------------------------------------------------------------------------

export interface LincolnMoneyGuardState {
  // Indemnity-surplus reserve. Indemnity cash above eligible cost banks here
  // and drains on later shortfall months (CM II / legacy parity). Always 0 on
  // reimbursement-election policies, so the surplus model is a no-op there.
  indemnitySurplus: number;
}
export interface LincolnMoneyGuardDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  baseRate: number;
  eobRate: number;
  capYears: number;
  isSimple: boolean;
}

// Each pool reads its own inflation rate, falling back to the policy-wide rate
// when the pool-specific knob is missing. Mirrors OA Asset Care. The
// simple-vs-compound election is policy-wide (one LTCBR inflation option), so
// both pools share it.
function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  return {
    baseRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    capYears: ltcPolicy.policyInflationProtectionYears ?? 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

function readAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  return {
    baseAtIssue: ltcPolicy.policyMaximumBenefitAmount ?? 0,
    eobAtIssue: ltcPolicy.policyContinuationBenefitAmount ?? 0,
  };
}

// Prior utilization draws the Face (base) pool down first; any excess beyond
// the base pool spills into the COB pool instead of being clamped away
// (mirrors indexedAnnuityCareStrategy's applyPriorUtilization).
function splitPriorUtilization(
  utilizedToDate: number,
  baseAtClaimStart: number,
  eobAtClaimStart: number,
): { base: number; eob: number } {
  const base = Math.min(Math.max(utilizedToDate, 0), baseAtClaimStart);
  const eob = Math.min(Math.max(utilizedToDate - base, 0), eobAtClaimStart);
  return { base, eob };
}

export const lincolnMoneyGuardStrategy: PolicyProjectionStrategy<
  LincolnMoneyGuardState,
  LincolnMoneyGuardDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<LincolnMoneyGuardState> {
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);
    // Anniversary inflation compounds every policy year regardless of whether
    // a claim has started; the projection only spans claim months, so
    // anniversaries between policy issue and claim start would otherwise be
    // silently skipped. Pre-inflate at claim start so opening pools match the
    // carrier's actual current pool. Claims starting at policy year 1 keep
    // both factors at 1.0; on the 0%-inflation illustration both factors are
    // always 1.0 regardless of claim-start year.
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart =
      eobAtIssue * growthFactor(eobRate, capYears, isSimple, claimStartPolicyYear);
    const prior = splitPriorUtilization(utilizedToDate, baseAtClaimStart, eobAtClaimStart);
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - prior.base, 0),
        extensionOfBenefitsPool: Math.max(eobAtClaimStart - prior.eob, 0),
        // The flat-Face residual DB opens net of prior utilization, floored —
        // a partially used policy opens reduced.
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization drawn Face (base) first, spilling into the COB pool
    // once the base is exhausted (mirrors `initClaimState`). Each leg is
    // capped at its pre-inflated pool at claim start.
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const baseAtClaimStart = baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart = eobAtIssue * growthFactor(eobRate, capYears, isSimple, claimStartPolicyYear);
    const prior = splitPriorUtilization(
      ltcPolicy.policyBenefitUtilizedToDate ?? 0,
      baseAtClaimStart,
      eobAtClaimStart,
    );
    const priorUtilization = prior.base + prior.eob;
    return {
      strategyId: 'lincoln-money-guard',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // MoneyGuard has no single product-wide inflation knob distinct from the
      // per-pool ones; populate inflationRate from the face/base rate for
      // legacy/UI compatibility.
      inflationRate: baseRate,
      inflationCapYears: capYears,
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // MoneyGuard ships dollar pools (Face Amount, Continuation of Benefits),
      // not a duration-derived model, so the duration fields stay unset and
      // the UI does not render a misleading "× N months" derivation.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);

    const baseFactor = growthFactor(baseRate, capYears, isSimple, policyYear);
    const eobFactor = growthFactor(eobRate, capYears, isSimple, policyYear);

    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<LincolnMoneyGuardState, LincolnMoneyGuardDeltas>,
  ): MonthlyEntry<LincolnMoneyGuardState, LincolnMoneyGuardDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate (see buildPrePolicyMonth). Single-life MoneyGuard floors
    // its policy year at 1 via getCalendarPolicyYear, so this is defensive — a
    // care month before the premium-start year pays $0, full cost OOP.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<LincolnMoneyGuardState, LincolnMoneyGuardDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // MoneyGuard's example illustration has a 0-day elimination period.
    // Routed by care environment through the two-tier WP fields like OA Asset
    // Care; with 0-day WPs this produces no waiting days.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's face/base factor). `costMetrics` and
    //    `policyYear` were resolved up front for the pre-policy gate.
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const monthlyCap = baseMonthly * growthFactor(baseRate, capYears, isSimple, policyYear);

    // 3. Annual anniversary accrual against the AT-ISSUE amount, gated on
    //    "pool still > 0" so an exhausted pool stays exhausted (the carrier
    //    caps lifetime LTC at what was drawn before exhaustion; inflated
    //    ceilings don't reopen a closed pool).
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        growthFactor(baseRate, capYears, isSimple, policyYear) -
        growthFactor(baseRate, capYears, isSimple, priorYear);
      const eobFactorDelta =
        growthFactor(eobRate, capYears, isSimple, policyYear) -
        growthFactor(eobRate, capYears, isSimple, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobAtIssue * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual =
      openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout: branches on the CLAIM-WIDE election (indemnity vs.
    //    reimbursement), NOT on which pool is funding the row. MoneyGuard has
    //    one LTC rider; the Face/COB split is only our modeling decomposition,
    //    so an indemnity election pays the capped indemnity cash from BOTH
    //    pools (Face first, then COB) until the total benefit exhausts — it
    //    never reverts to reimbursement on COB.
    //
    //    INDEMNITY BENEFIT REDUCTION: Lincoln caps cash indemnity at 80% of the
    //    Monthly Maximum (illustration p.4 fn 5 / p.9). Like Asset Care's 75%
    //    cap, this is a fixed product property carried on the policy as
    //    `policyIndemnityBenefitPercent` (the upload field map fixes it at 0.80
    //    — see policyUploadPolicyFieldMap.ts). The strategy is product-agnostic:
    //    it applies whatever percent the policy carries, defaulting to 1.0 when
    //    absent. So an uploaded MoneyGuard pays 80% under an indemnity election;
    //    a hand-entered policy with no percent pays 100% until one is set.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const indemnityPercent = ltcPolicy.policyIndemnityBenefitPercent ?? 1;
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;
    if (isIndemnity) {
      // Claim-wide indemnity: pay the capped cash from Face first, then COB at
      // the same indemnity cap. Each pool's draw is bounded by its balance, so
      // the last paying month may pay less than the capped amount.
      const indemnityTarget = proRatedCap * indemnityPercent;
      drawFromBase = Math.min(indemnityTarget, baseAfterAccrual);
      drawFromEob = Math.min(indemnityTarget - drawFromBase, eobAfterAccrual);
      indemnityPaid = drawFromBase + drawFromEob;
    } else {
      // Reimbursement election: pay min(eligibleCost, proRatedCap), drawn Face
      // first then COB.
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromBase = Math.min(wouldPay, baseAfterAccrual);
      drawFromEob = Math.min(wouldPay - drawFromBase, eobAfterAccrual);
    }
    // Gross cash this row pays out across both pools. The pools deplete by this
    // gross cash; the surplus model is a separate accounting reserve on top.
    const rowPayout = drawFromBase + drawFromEob;

    // 4b. Indemnity-surplus accounting (per-row). Cost-coverage is capped at
    //     eligible cost; cash above eligible cost banks as surplus and drains
    //     on later shortfall rows. On reimbursement-election policies the open
    //     surplus is always 0 and the reimbursed payout never exceeds eligible
    //     cost, so this is a numeric no-op (no accrual, no drain).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools (Face + COB): exhausted only when BOTH are spent.
      policyExhausted: baseAfterAccrual + eobAfterAccrual <= 0,
    });

    // 5. Closing balances. Only Face (base) drawdown depletes the death
    //    benefit (COB is a separate pool and doesn't accelerate the face).
    //    Floor at the Residual Death Benefit.
    const closeBase = baseAfterAccrual - drawFromBase;
    const closeEob = eobAfterAccrual - drawFromEob;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - drawFromBase,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      // MoneyGuard's single monthly cap grows on the base rate for both pools
      // (no per-pool cap switch in this engine's model).
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<LincolnMoneyGuardDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<LincolnMoneyGuardState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<
      LincolnMoneyGuardState,
      LincolnMoneyGuardDeltas
    >({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<LincolnMoneyGuardState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<LincolnMoneyGuardState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<LincolnMoneyGuardState>): number {
    return closingState.pools.deathBenefit;
  },
};
